import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckSquare, ChevronLeft, Plus, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getChecklistTemplates, completeChecklist, GetChecklistTemplatesOutputType } from 'zite-endpoints-sdk';
import ChecklistRunner from '@/components/tools/ChecklistRunner';

type Template = GetChecklistTemplatesOutputType['templates'][0];
type Completion = GetChecklistTemplatesOutputType['completions'][0];
type View = 'list' | 'running';

const TYPE_COLORS: Record<string, string> = {
  'Opening Checks': 'bg-green-500/10 text-green-600 dark:text-green-400',
  'Closing Checks': 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  'Bar Checks': 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  'Fire Checks': 'bg-red-500/10 text-red-600 dark:text-red-400',
  'Cleaning': 'bg-teal-500/10 text-teal-600 dark:text-teal-400',
  'Kitchen Handover': 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  'Cellar Checks': 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  'Event Checks': 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  'Completed': <CheckCircle className="w-4 h-4 text-green-500" />,
  'Signed Off': <CheckCircle className="w-4 h-4 text-blue-500" />,
  'In Progress': <Clock className="w-4 h-4 text-amber-500" />,
};

export default function Checklists() {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState<Template | null>(null);
  const [activeCompletion, setActiveCompletion] = useState<Completion | undefined>(undefined);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getChecklistTemplates({}).then(d => {
      setTemplates(d.templates);
      setCompletions(d.completions);
    }).catch(() => toast.error('Failed to load checklists'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (d: { completionId?: string; data: object; status: string; signedOff: boolean }) => {
    if (!activeTemplate) return;
    try {
      await completeChecklist({
        id: d.completionId,
        templateId: activeTemplate.id,
        title: `${activeTemplate.title} — ${format(new Date(), 'dd MMM yyyy')}`,
        data: JSON.stringify(d.data),
        status: d.status as any,
        signedOff: d.signedOff,
      });
      toast.success(d.status === 'Completed' || d.status === 'Signed Off' ? 'Checklist completed! ✓' : 'Progress saved');
      load();
      if (d.status === 'Completed' || d.status === 'Signed Off') setView('list');
    } catch { toast.error('Failed to save'); }
  };

  const startChecklist = (template: Template) => {
    const existing = completions.find(c => c.templateId === template.id && c.status === 'In Progress');
    setActiveTemplate(template);
    setActiveCompletion(existing);
    setView('running');
  };

  const filtered = templates.filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.type.toLowerCase().includes(search.toLowerCase()));

  if (view === 'running' && activeTemplate) return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <ChecklistRunner
        template={activeTemplate}
        existing={activeCompletion}
        onSave={handleSave}
        onBack={() => setView('list')}
        userName={`${user?.preferredName || user?.firstName || ''} ${user?.lastName || ''}`.trim()}
      />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tools" className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Checklists</h1>
          <p className="text-sm text-muted-foreground">Complete and track operational checklists digitally</p>
        </div>
        {(user?.role === 'Manager' || user?.role === 'Admin') && (
          <Link to="/admin"><Button variant="outline" size="sm"><Plus className="w-4 h-4 mr-1.5" /> Manage Templates</Button></Link>
        )}
      </div>

      <Input placeholder="Search checklists..." value={search} onChange={e => setSearch(e.target.value)} className="mb-4" />

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <CheckSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">No checklists available</h3>
          <p className="text-muted-foreground text-sm">
            {templates.length === 0 ? 'Ask a manager to add checklist templates in the Admin Panel.' : 'No checklists match your search.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(t => {
            const inProgress = completions.find(c => c.templateId === t.id && c.status === 'In Progress');
            const typeClass = TYPE_COLORS[t.type] || 'bg-muted text-muted-foreground';
            return (
              <Card key={t.id} className="p-4 flex items-center gap-4 hover:border-primary/40 transition-colors">
                <div className={`p-2.5 rounded-xl shrink-0 ${typeClass}`}>
                  <CheckSquare className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-semibold">{t.title}</p>
                    {t.requiresSignoff && <Badge variant="outline" className="text-xs">Signoff required</Badge>}
                    {t.minRole !== 'Staff' && <Badge variant="outline" className="text-xs">{t.minRole}+</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{t.type}</p>
                </div>
                {inProgress && (
                  <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 shrink-0">
                    <Clock className="w-3.5 h-3.5" /> In progress
                  </div>
                )}
                <Button size="sm" onClick={() => startChecklist(t)} className="shrink-0">
                  {inProgress ? 'Continue' : 'Start'}
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {completions.length > 0 && (
        <div className="mt-8">
          <h2 className="font-semibold text-base mb-3">Recent Completions</h2>
          <div className="space-y-2">
            {completions.slice(0, 5).map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 border border-border rounded-xl text-sm">
                {STATUS_ICON[c.status] || <XCircle className="w-4 h-4 text-muted-foreground" />}
                <span className="flex-1 font-medium truncate">{c.title}</span>
                <span className="text-muted-foreground text-xs">{c.completionDate ? format(new Date(c.completionDate), 'dd MMM') : ''}</span>
                <Badge variant="outline" className="text-xs">{c.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
